import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaExternalLinkAlt, FaChevronRight, FaTimes } from 'react-icons/fa';
import { db } from '../../firebase';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import PromotionModal from './PromotionModal';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [promotion, setPromotion] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const headerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        document.documentElement.style.setProperty(
          '--header-height',
          `${headerRef.current.offsetHeight}px`
        );
      }
    };

    updateHeaderHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeaderHeight();
    });

    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [promotion, isBannerVisible]);

  useEffect(() => {
    const fetchActivePromotion = async () => {
      try {
        const q = query(
          collection(db, 'promotions'),
          where('isActive', '==', true),
          limit(1)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          setPromotion({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
        }
      } catch (error) {
        console.error('Erro ao buscar promoção activa:', error);
      }
    };

    fetchActivePromotion();
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const toggleSubmenu = (index) => {
    setOpenSubmenu(openSubmenu === index ? null : index);
  };

  const menuItems = [
    { href: "#features", label: "Recursos" },
    { href: "#process", label: "Como Funciona" },
    { href: "#testimonials", label: "Clientes" },
    {
      label: "Parceiros",
      hasSubmenu: true,
      submenu: [
        {
          href: "https://forms.gle/Ho1hQ2mvso7BrQgs5",
          label: "Seja Parceiro",
          icon: <FaExternalLinkAlt className="inline mr-2 text-xs" />,
          external: true
        }
      ]
    },
    { href: "#faq", label: "FAQ" },
  ];

  return (
    <motion.header
      ref={headerRef}
      className={`header fixed top-0 left-0 w-full z-50 transition-all duration-500 ${isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100'
          : 'bg-white/95 backdrop-blur-md'
        }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* Top Announcement Bar - Dynamic */}
      {promotion && isBannerVisible && (
        <div className="w-full min-h-[40px] py-2 px-4 flex justify-center items-center text-xs sm:text-sm relative z-20 transition-colors pr-10" style={{ backgroundColor: promotion.bannerColor || '#000000' }}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 justify-center w-full max-w-7xl text-center">
            {promotion.highlightText && (
              <span className="shrink-0 bg-[#064e3b]/50 text-[#34d399] border border-[#065f46] px-2.5 py-0.5 rounded-full font-bold text-[10px] tracking-wider uppercase flex items-center justify-center">
                {promotion.highlightText}
              </span>
            )}
            <span className="text-gray-200 font-medium leading-tight sm:leading-normal">
              {promotion.title} {promotion.description && <span className="opacity-90 font-normal hidden sm:inline ml-1">- {promotion.description}</span>}
            </span>
            {promotion.description && <span className="text-gray-300 opacity-90 font-normal block sm:hidden w-full text-[11px] mt-0.5">{promotion.description}</span>}
            <button onClick={() => setIsModalOpen(true)} className="shrink-0 text-white hover:text-gray-200 underline underline-offset-4 transition-colors flex items-center justify-center gap-1 font-bold outline-none">
              {promotion.ctaText || 'Participar'} <FaChevronRight className="text-[9px] ml-0.5 mt-[1px]" />
            </button>
          </div>
          <button 
            onClick={() => setIsBannerVisible(false)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors outline-none"
            aria-label="Fechar banner"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Background Pattern - Subtle diagonal stripes for visual depth */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03] -z-10"
        style={{
          backgroundImage: `repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)`,
          backgroundSize: '10px 10px'
        }}
      ></div>

      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <motion.div
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <Link to="/" className="flex items-center group">
              <img
                src={logo}
                alt="CriaApp Logo"
                className="h-12 w-auto transition-all duration-500 rounded-tr-xl rounded-bl-xl grayscale contrast-125 opacity-80 group-hover:grayscale-0 group-hover:contrast-100 group-hover:opacity-100"
              />
            </Link>
          </motion.div>

          {/* Navegação desktop */}
          <nav className="hidden md:flex items-center space-x-6">
            {menuItems.map((item, index) => (
              <div key={index} className="relative">
                {item.hasSubmenu ? (
                  /* Item com submenu */
                  <motion.div
                    className="relative text-gray-600 font-medium py-2 transition-all duration-300 group hover:text-gray-900 cursor-pointer flex items-center"
                    whileHover={{ y: -1 }}
                    transition={{ duration: 0.2 }}
                    onHoverStart={() => setOpenSubmenu(index)}
                    onHoverEnd={() => setOpenSubmenu(null)}
                  >
                    {item.label}
                    <FaChevronDown className="ml-1 text-xs transition-transform duration-300 group-hover:rotate-180" />
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gray-900 transition-all duration-300 group-hover:w-full"></span>

                    {/* Submenu Dropdown */}
                    <motion.div
                      className="absolute top-full left-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-100 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{
                        opacity: openSubmenu === index ? 1 : 0,
                        y: openSubmenu === index ? 0 : 10
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      {item.submenu.map((subItem, subIndex) => (
                        <a
                          key={subIndex}
                          href={subItem.href}
                          target={subItem.external ? "_blank" : undefined}
                          rel={subItem.external ? "noopener noreferrer" : undefined}
                          className="flex items-center px-4 py-3 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 font-medium border-b border-gray-50 last:border-b-0"
                        >
                          {subItem.icon}
                          {subItem.label}
                        </a>
                      ))}
                    </motion.div>
                  </motion.div>
                ) : (
                  /* Item normal */
                  <motion.a
                    href={item.href}
                    className="relative text-gray-600 font-medium py-2 transition-all duration-300 group hover:text-gray-900"
                    whileHover={{ y: -1 }}
                    transition={{ duration: 0.2 }}
                  >
                    {item.label}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gray-900 transition-all duration-300 group-hover:w-full"></span>
                  </motion.a>
                )}
              </div>
            ))}

            {/* Separador */}
            <span className="text-gray-200 font-medium text-xl">|</span>

            {/* Botão CTA */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                to="/login"
                className="relative bg-gray-900 hover:bg-black text-white px-5 py-2.5 rounded-lg font-medium transition-all duration-300 shadow-md group block border border-gray-800"
              >
                <span className="relative z-10">Criar Aplicativo</span>
              </Link>
            </motion.div>
          </nav>

          {/* Botão menu mobile */}
          <motion.button
            className="md:hidden p-3 rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-all duration-300 border border-gray-100"
            onClick={toggleMenu}
            aria-label="Menu"
            whileTap={{ scale: 0.9 }}
          >
            <div className="w-5 h-5 relative">
              <span className={`absolute left-0 top-1 w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'rotate-45 top-2' : ''}`}></span>
              <span className={`absolute left-0 top-2.5 w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`absolute left-0 top-4 w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? '-rotate-45 top-2' : ''}`}></span>
            </div>
          </motion.button>
        </div>

        {/* Menu mobile dropdown com animação */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 mt-2 rounded-2xl shadow-xl overflow-hidden"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col space-y-1 p-4">
                {menuItems.map((item, index) => (
                  <div key={index}>
                    {item.hasSubmenu ? (
                      /* Item com submenu no mobile */
                      <div className="py-2">
                        <button
                          className="flex items-center justify-between w-full text-gray-700 hover:text-gray-900 font-medium py-2 px-4 rounded-xl hover:bg-gray-50 transition-all duration-200"
                          onClick={() => toggleSubmenu(index)}
                        >
                          <span>{item.label}</span>
                          <FaChevronDown className={`text-xs transition-transform duration-300 ${openSubmenu === index ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                          {openSubmenu === index && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="ml-4 mt-1"
                            >
                              {item.submenu.map((subItem, subIndex) => (
                                <a
                                  key={subIndex}
                                  href={subItem.href}
                                  target={subItem.external ? "_blank" : undefined}
                                  rel={subItem.external ? "noopener noreferrer" : undefined}
                                  className="flex items-center text-gray-500 hover:text-gray-900 font-medium py-2 px-4 rounded-xl hover:bg-gray-50 transition-all duration-200"
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  {subItem.icon}
                                  {subItem.label}
                                </a>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      /* Item normal no mobile */
                      <motion.a
                        href={item.href}
                        className="text-gray-700 hover:text-gray-900 font-medium py-3 px-4 rounded-xl hover:bg-gray-50 transition-all duration-200 block"
                        whileHover={{ x: 5 }}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.label}
                      </motion.a>
                    )}
                  </div>
                ))}

                {/* Separador no mobile */}
                <div className="border-t border-gray-100 my-3"></div>

                {/* Botão CTA mobile */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    to="/login"
                    className="bg-gray-900 hover:bg-black text-white py-3 px-4 rounded-xl font-medium text-center transition-all duration-300 block shadow-md"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Criar Aplicativo
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <PromotionModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        promotion={promotion} 
      />
    </motion.header>
  );
};

export default Header;